import {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

interface Position {
  x: number;
  y: number;
}

const useDraggableModal = (isOpen: boolean, isDragEnabled = true) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef({
    pointerId: 0,
    startX: 0,
    startY: 0,
    startPositionX: 0,
    startPositionY: 0,
    minPositionY: 0,
  });

  const onPointerMove = useCallback((event: PointerEvent | globalThis.PointerEvent) => {
    const modal = modalRef.current;

    if (!modal || event.pointerId !== dragStateRef.current.pointerId) {
      return;
    }

    const { startX, startY, startPositionX, startPositionY, minPositionY } = dragStateRef.current;
    const deltaX = event.clientX - startX;
    const deltaY = event.clientY - startY;

    setPosition({
      x: startPositionX + deltaX,
      y: Math.max(startPositionY + deltaY, minPositionY),
    });
  }, []);

  const stopDragging = useCallback(() => {
    setIsDragging(false);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', stopDragging);
    window.removeEventListener('pointercancel', stopDragging);
  }, [onPointerMove]);

  const onDragHandlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!isDragEnabled) {
        return;
      }

      if (event.button !== 0) {
        return;
      }

      const target = event.target as HTMLElement;

      if (
        target.closest('button, input, select, textarea, a, [role="button"], [data-no-drag="true"]')
      ) {
        return;
      }

      const isModalMaximized = modalRef.current
        ?.closest('.modal-overlay')
        ?.classList.contains('modal-maximized');

      if (isModalMaximized) {
        return;
      }

      const rect = modalRef.current?.getBoundingClientRect();

      if (!rect) {
        return;
      }

      dragStateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startPositionX: position.x,
        startPositionY: position.y,
        minPositionY: position.y - rect.top,
      };

      setIsDragging(true);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', stopDragging);
      window.addEventListener('pointercancel', stopDragging);
    },
    [isDragEnabled, onPointerMove, position.x, position.y, stopDragging],
  );

  useEffect(() => {
    if (isOpen) {
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  useEffect(() => () => stopDragging(), [stopDragging]);

  useEffect(() => {
    if (!isDragEnabled) {
      stopDragging();
    }
  }, [isDragEnabled, stopDragging]);

  const modalStyle: CSSProperties = {
    transform: `translate(${position.x}px, ${position.y}px)`,
  };

  return {
    modalRef,
    modalStyle,
    isDragging,
    dragHandleProps: {
      onPointerDown: onDragHandlePointerDown,
    },
  };
};

export default useDraggableModal;
